import { useCallback } from "react";
import { urlParams } from "../constants/AppConstants";
import useRecordService from "../pages/recordManagement/useRecordService";

const parseApiData = (response) => {
    if (!response) return null;
    try {
        const parsed = JSON.parse(response);
        const data = parsed?.data?.data ?? parsed?.data;
        return typeof data === "string" ? JSON.parse(data) : data;
    } catch {
        return null;
    }
};

const PHONE_FIELD_NAMES = ["phone", "mobile", "primary_phone", "secondary_phone", "phone_number"];

const extractFromRecord = (record) => {
    const valuesMap = {};
    (record?.values || []).forEach((item) => {
        if (item?.key != null) valuesMap[item.key] = item.value;
    });
    const firstName = valuesMap.first_name || valuesMap.firstName || "";
    const lastName = valuesMap.last_name || valuesMap.lastName || "";
    let phone = "";
    for (const fn of PHONE_FIELD_NAMES) {
        if (valuesMap[fn]) {
            phone = String(valuesMap[fn]);
            break;
        }
    }
    return { id: record?.id, firstName, lastName, phone };
};

const toContactOption = (c) => ({
    fieldApiName: "phone",
    fieldName: [c.firstName, c.lastName].filter(Boolean).join(" ") || c.phone || c.id,
    fieldValue: c.phone,
    fieldType: "phone",
    crmRecordId: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
});

export default function useTeamsRecipients(appProperties, fetchData) {
    const controller = appProperties?.controller;
    const { searchRecords } = useRecordService();

    const fetchChannelRecipients = useCallback(
        async ({ teamId, channelId, chatId }) => {
            const leftServiceId = appProperties?.leftServiceId || urlParams.get("leftServiceId");
            if (!leftServiceId) return [];
            const params = new URLSearchParams({ leftServiceId });
            if (teamId) params.set("teamId", teamId);
            if (channelId) params.set("channelId", channelId);
            if (chatId) params.set("chatId", chatId);
            const response = await fetchData(`teams/channel/recipients?${params}`, "GET", null, appProperties);
            const data = parseApiData(response);
            if (!data) return [];
            const arr = Array.isArray(data) ? data : [];
            return arr.map((m) => toContactOption({
                id: m.crmRecordId,
                phone: m.phone,
                firstName: m.firstName,
                lastName: m.lastName,
            }));
        },
        [appProperties, fetchData]
    );

    const searchCrmContacts = useCallback(
        async (searchText, signal = null) => {
            if (!searchText?.trim()) return [];
            try {
                const payload = { moduleName: "Contacts", searchText: searchText.trim(), onlyDefault: false };
                const response = await searchRecords(payload, 0, 20, "created_at:desc", signal);
                const content = response?.data?.content || [];
                return content
                    .map((r) => extractFromRecord(r))
                    .filter((c) => c.phone)
                    .map((c) => toContactOption(c));
            } catch (err) {
                if (err?.code !== "ERR_CANCELED") {
                    console.error("CRM search failed:", err);
                }
                return [];
            }
        },
        [searchRecords]
    );

    const loadRecipients = useCallback(
        (setToPhoneNumberList, setToNumber, setLoading) => {
            if (!controller || controller?.service !== "Teams") {
                setLoading?.(false);
                return Promise.resolve();
            }
            setLoading?.(true);
            return controller
                .testing()
                .then(async (context) => {
                    const channelId = context?.channel?.id;
                    const teamId = context?.team?.groupId;
                    const chatId = context?.chat?.id;
                    const prev = await fetchChannelRecipients({ teamId, channelId, chatId });
                    return prev;
                })
                .then((list) => {
                    setToPhoneNumberList?.(list || []);
                })
                .catch(() => setToPhoneNumberList?.([]))
                .finally(() => setLoading?.(false));
        },
        [controller, fetchChannelRecipients]
    );

    return { loadRecipients, searchCrmContacts };
}
